import * as Sentry from '@sentry/browser';
import { datadogRum } from '@datadog/browser-rum';

import PropTypes from 'prop-types';
import React from 'react';
import ReactDOM from 'react-dom';

import './styles/main.scss';

import i18n, { aliases } from './i18n/i18n';
import { fetcher } from '@weezevent/weezjs-core';
import Storage from './helpers/storage';
import { PermissionsLayer } from './user/permissions';

import App from './app';
import { BrowserRouter as Router, Route } from 'react-router-dom';
import { ToastProvider, MixPanelProvider } from '@weezevent/nacre';

import AppContextProvider from './context/appContextProvider';
import Loading from './components/loading';

import { SimpleConnect } from './simple-connect';
import ErrorBoundary from './components/errorBoundary';
import TrackingProvider from './components/tracking/trackingProvider';
import NotificationsProvider from './components/notifications/notificationsProvider';

// Devs should not add sentry to localconfig to prevent useless errors
if (window.weezConfig.sentry) {
    Sentry.init({
        dsn: window.weezConfig.sentry.dsn,
        beforeSend: (event, hint) => {
            if (hint.originalException?.code === 403) {
                return null;
            }

            return event;
        },
        environment: window.location.host,
        release: 'willy-' + window.weezConfig.version
    });
}

if (window.weezConfig.datadog) {
    datadogRum.init({
        applicationId: window.weezConfig.datadog.applicationId,
        clientToken: window.weezConfig.datadog.clientToken,
        env: window.weezConfig.datadog.env,
        site: window.weezConfig.datadog.site,
        service: window.weezConfig.datadog.service,
        sampleRate: window.weezConfig.datadog.sampleRate,
        sessionReplaySampleRate: window.weezConfig.datadog.sessionReplaySampleRate,
        trackInteractions: window.weezConfig.datadog.trackInteractions,
        trackResources: window.weezConfig.datadog.trackResources,
        trackLongTasks: window.weezConfig.datadog.trackLongTasks,
        defaultPrivacyLevel: window.weezConfig.datadog.defaultPrivacyLevel,
        version: window.weezConfig.version,
        enableExperimentalFeatures: ['clickmap']
    });
    datadogRum.startSessionReplayRecording();
}

export default class Willy extends React.Component {
    constructor(...args) {
        super(...args);

        this.state = {
            connect: null,
            connectData: null
        };

        this.storage = Storage;

        // Redundant until we can remove internal fetcher
        const apiUrl = window.weezConfig.api.apiUrl;
        fetcher.initialize(apiUrl);
    }

    setNewLocale(userLocale) {
        if (!userLocale) {
            return;
        }

        const accountsTokenLocale = userLocale.replace(/_/g, '-');
        const locale = aliases[accountsTokenLocale] ?? accountsTokenLocale;
        i18n.accountLocale = accountsTokenLocale;
        if (locale !== i18n.language) {
            i18n.changeLanguage(locale);
            fetcher.setLanguage(locale);
        }
    }

    render() {
        return (
            <React.Suspense fallback={<Loading />}>
                <ToastProvider>
                    <MixPanelProvider token={window.weezConfig.mixpanel}>
                        <Router>
                            <SimpleConnect
                                onToken={(token, parsed_token) => {
                                    fetcher.setToken(token);
                                    this.setNewLocale(parsed_token.payload?.data.locale);
                                }}
                            >
                                <PermissionsLayer>
                                    <TrackingProvider token={window.weezConfig.segment}>
                                        <NotificationsProvider>
                                            <AppContextProvider>
                                                <div className="sauron">
                                                    <div className="app-route">
                                                        <Route path="/">
                                                            <ErrorBoundary location={window.location}>
                                                                <App />
                                                            </ErrorBoundary>
                                                        </Route>
                                                    </div>
                                                </div>
                                            </AppContextProvider>
                                        </NotificationsProvider>
                                    </TrackingProvider>
                                </PermissionsLayer>
                            </SimpleConnect>
                        </Router>
                    </MixPanelProvider>
                </ToastProvider>
            </React.Suspense>
        );
    }
}

Willy.propTypes = {
    apiUrl: PropTypes.string,
    clientId: PropTypes.string.isRequired,
    oAuthUrl: PropTypes.string.isRequired
};

Willy.defaultProps = {
    apiUrl: null
};

ReactDOM.render(
    <Willy menhirUrl={window.weezConfig.temporary.menhirUrl} clientId={window.weezConfig.connect.clientId} oAuthUrl={window.weezConfig.connect.oAuthUrl} />,
    document.getElementById('main')
);
